export const FG_IMPLICIT_LIST_CLASS = 'fg-implicit-list';

function isInsideRealList(el: Element): boolean {
  return Boolean(el.closest('ul,ol,li,dl,dt,dd'));
}

/**
 * Publisher/EPUB HTML sometimes renders lists as a sequence of paragraphs (e.g. references `p.ref`).
 * Convert consecutive `p.ref` blocks into a real ordered list so AT announces list semantics.
 *
 * This is intentionally narrow to avoid unintended layout changes.
 */
export function enhanceImplicitListsInHtml(html: string): string {
  if (
    typeof document === 'undefined' ||
    typeof DOMParser === 'undefined' ||
    !/class\s*=\s*["'][^"']*\bref\b[^"']*["']/i.test(html)
  ) {
    return html;
  }

  try {
    const doc = new DOMParser().parseFromString(`<div data-fg-root="1">${html}</div>`, 'text/html');
    const root = doc.querySelector('[data-fg-root="1"]');
    if (!root) {
      return html;
    }

    const refs = Array.from(root.querySelectorAll('p.ref'));

    for (const ref of refs) {
      // skip if already handled or if embedded in a real list
      if (!(ref instanceof Element) || isInsideRealList(ref)) {
        continue;
      }

      // Only start a group at the first in a consecutive run.
      const prev = ref.previousElementSibling;
      if (prev?.matches?.('p.ref')) {
        continue;
      }

      const group: Element[] = [ref];
      let next = ref.nextElementSibling;
      while (next && next.matches('p.ref') && !isInsideRealList(next)) {
        group.push(next);
        next = next.nextElementSibling;
      }

      if (group.length < 2) {
        continue;
      }

      const ol = doc.createElement('ol');
      ol.className = FG_IMPLICIT_LIST_CLASS;

      const first = group[0];
      first.parentElement?.insertBefore(ol, first);

      for (const p of group) {
        const li = doc.createElement('li');
        // Preserve ids/classes in case other features anchor to them.
        for (const attr of Array.from(p.attributes)) {
          li.setAttribute(attr.name, attr.value);
        }
        // Keep original `.ref` class for styling hooks; add our marker too.
        li.classList.add('ref', FG_IMPLICIT_LIST_CLASS);

        while (p.firstChild) {
          li.appendChild(p.firstChild);
        }
        ol.appendChild(li);
        p.remove();
      }
    }

    return root.innerHTML;
  } catch {
    return html;
  }
}

